import React, { FC, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'

import { Flex, Table, theme, Typography } from 'antd'
import { useResponsive } from 'antd-style'
import { ColumnType } from 'antd/es/table'

import { RiderNumber } from './RiderNumber'

const Career: FC<{ career: MotoGP.RiderCareer[]; statistics: MotoGP.RiderStatistics[] }> = ({ career, statistics }) => {
  const { token } = theme.useToken()
  const { desktop } = useResponsive()
  const dataSource = career.map((it, id) => ({ ...it, id }))

  const getStatsByYear = useCallback(
    (year: number, category: string) =>
      statistics.find((it) => it.season == year && it.category.replace('™', '') === category.replace('™', '')),
    [statistics],
  )
  const categories: Record<string, string> = dataSource
    .map((it) => it.category)
    .reduce((acc, cur) => ({ ...acc, ...{ [cur.id]: cur.name } }), {})
  const bikes: Record<string, string> = dataSource
    .map((it) => it.team?.constructor)
    .filter(Boolean)
    .reduce((acc, cur) => ({ ...acc, ...{ [`${cur?.id}`]: cur?.name } }), {})
  const teams: Record<string, string> = dataSource
    .map((it) => it.team)
    .filter(Boolean)
    .reduce((acc, cur) => ({ ...acc, ...{ [`${cur?.id}`]: cur?.name } }), {})

  const columns: ColumnType<MotoGP.RiderCareer>[] = [
    {
      key: 'rider.career.year',
      render: (it: MotoGP.RiderCareer) => it.season,
      fixed: 'left',
      align: 'center',
    },
    {
      key: 'rider.career.category',
      render: (it: MotoGP.RiderCareer) => it.category.name,
      filters: Object.entries(categories).map(([value, text]) => ({
        text,
        value,
      })),
      width: 100,
      onFilter: (value: string, record: MotoGP.RiderCareer) => record.category.id === value,
    },
    {
      key: 'rider.career.number',
      align: 'center',
      width: 75,
      render: (it: MotoGP.RiderCareer) => <RiderNumber number={it.number} color={it.team?.color} />,
    },
    {
      key: 'rider.career.team',
      render: (it: MotoGP.RiderCareer) => <Typography.Text ellipsis>{it.team?.name}</Typography.Text>,
      filters: Object.entries(teams).map(([value, text]) => ({
        text,
        value,
      })),
      width: 120,
      onFilter: (value: string, record: MotoGP.RiderCareer) => record?.team?.id === value,
    },
    {
      key: 'rider.career.bike',
      render: (it: MotoGP.RiderCareer) => <Typography.Text ellipsis>{it.team?.constructor?.name}</Typography.Text>,
      filters: Object.entries(bikes).map(([value, text]) => ({
        text,
        value,
      })),
      width: 80,
      onFilter: (value: string, record: MotoGP.RiderCareer) => record?.team?.constructor?.id === value,
    },
    {
      key: 'rider.career.position',
      align: 'center',
      width: 100,
      render: (item: MotoGP.RiderCareer) => (
        <Flex
          style={{
            justifyContent: 'center',
            fontWeight: getStatsByYear(item.season, item.category.name)?.position === 1 ? 600 : 400,
            color:
              getStatsByYear(item.season, item.category.name)?.position === 1 ? token.colorPrimary : token.colorText,
          }}
          gap={8}
          align={'center'}
        >
          {getStatsByYear(item.season, item.category.name)?.position}
        </Flex>
      ),
      sorter: (a: MotoGP.RiderCareer, b: MotoGP.RiderCareer) =>
        (getStatsByYear(a.season, a.category.name)?.position ?? 0) -
        (getStatsByYear(b.season, b.category.name)?.position ?? 0),
    },
    {
      key: 'rider.career.races',
      align: 'center',
      width: 100,
      render: (item: MotoGP.RiderCareer) => getStatsByYear(item.season, item.category.name)?.starts,
      sorter: (a: MotoGP.RiderCareer, b: MotoGP.RiderCareer) =>
        (getStatsByYear(a.season, a.category.name)?.starts ?? 0) -
        (getStatsByYear(b.season, b.category.name)?.starts ?? 0),
    },
    {
      key: 'rider.career.points',
      align: 'center',
      width: 100,
      render: (item: MotoGP.RiderCareer) => getStatsByYear(item.season, item.category.name)?.points,
      sorter: (a: MotoGP.RiderCareer, b: MotoGP.RiderCareer) =>
        (getStatsByYear(a.season, a.category.name)?.points ?? 0) -
        (getStatsByYear(b.season, b.category.name)?.points ?? 0),
    },

    {
      key: 'rider.career.poles',
      align: 'center',
      width: 100,
      render: (item: MotoGP.RiderCareer) => statistics.find((it) => it.season == item.season)?.poles,
      sorter: (a: MotoGP.RiderCareer, b: MotoGP.RiderCareer) =>
        (getStatsByYear(a.season, a.category.name)?.poles ?? 0) -
        (getStatsByYear(b.season, b.category.name)?.poles ?? 0),
    },
    {
      key: 'rider.career.podiums',
      align: 'center',
      width: 120,
      render: (item: MotoGP.RiderCareer) => statistics.find((it) => it.season == item.season)?.podiums,
      sorter: (a: MotoGP.RiderCareer, b: MotoGP.RiderCareer) =>
        (getStatsByYear(a.season, a.category.name)?.podiums ?? 0) -
        (getStatsByYear(b.season, b.category.name)?.podiums ?? 0),
    },
    {
      key: 'rider.career.victories',
      align: 'center',
      width: 120,
      render: (item: MotoGP.RiderCareer) => statistics.find((it) => it.season == item.season)?.first_position,
      sorter: (a: MotoGP.RiderCareer, b: MotoGP.RiderCareer) =>
        (getStatsByYear(a.season, a.category.name)?.first_position ?? 0) -
        (getStatsByYear(b.season, b.category.name)?.first_position ?? 0),
    },
  ].map((it) => ({ ...it, title: <FormattedMessage id={it.key} /> })) as ColumnType<MotoGP.RiderCareer>[]
  return (
    <Table
      bordered
      rowKey={'id'}
      scroll={!desktop ? { y: 'calc(100vh - 300px)', x: 'max-content' } : undefined}
      size={'small'}
      onRow={() => ({ style: { backgroundColor: token.colorBgContainer } })}
      columns={columns}
      dataSource={dataSource}
      pagination={false}
      summary={(data) => {
        const totalPoles = data.reduce(
          (acc, cur) => acc + (getStatsByYear(cur.season, cur.category.name)?.poles ?? 0),
          0,
        )
        const totalPodiums = data.reduce(
          (acc, cur) => acc + (getStatsByYear(cur.season, cur.category.name)?.podiums ?? 0),
          0,
        )
        const totalWins = data.reduce(
          (acc, cur) => acc + (getStatsByYear(cur.season, cur.category.name)?.first_position ?? 0),
          0,
        )
        const totalPoints = data.reduce(
          (acc, cur) => acc + (getStatsByYear(cur.season, cur.category.name)?.points ?? 0),
          0,
        )
        const totalRaces = data.reduce(
          (acc, cur) => acc + (getStatsByYear(cur.season, cur.category.name)?.starts ?? 0),
          0,
        )
        return (
          <Table.Summary.Row style={{ background: token.Table?.headerBg }}>
            <Table.Summary.Cell index={0}>Всього</Table.Summary.Cell>
            <Table.Summary.Cell index={1}></Table.Summary.Cell>
            <Table.Summary.Cell index={2}></Table.Summary.Cell>
            <Table.Summary.Cell index={3}></Table.Summary.Cell>
            <Table.Summary.Cell index={4}></Table.Summary.Cell>
            <Table.Summary.Cell index={5}></Table.Summary.Cell>
            <Table.Summary.Cell align={'center'} index={6}>
              <Typography.Text type={'warning'} strong>
                {totalRaces}
              </Typography.Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell align={'center'} index={7}>
              <Typography.Text type={'warning'} strong>
                {totalPoints}
              </Typography.Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell align={'center'} index={8}>
              <Typography.Text type={'warning'} strong>
                {totalPoles}
              </Typography.Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell align={'center'} index={9}>
              <Typography.Text type={'warning'} strong>
                {totalPodiums}
              </Typography.Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell align={'center'} index={10}>
              <Typography.Text type={'warning'} strong>
                {totalWins}
              </Typography.Text>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )
      }}
    />
  )
}

export { Career }
